import React from 'react';
import { TrendingUp, FileText, Send, Upload, Home, Clock } from 'lucide-react';
import { MetricTile } from './MetricTile';
import { motion } from 'framer-motion';
import { LoanStage } from '../data/loans';
interface MonthlyScorecardProps {
  onTileClick?: (stage: LoanStage) => void;
}
export function MonthlyScorecard({
  onTileClick
}: MonthlyScorecardProps = {}) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: 0.2
  }} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col h-full" data-id="element-145">
      <div className="bg-[#0a1e5e] px-4 py-3 flex items-center justify-between gap-3 text-white" data-id="element-146">
        <div className="flex items-center gap-2 min-w-0" data-id="element-147">
          <TrendingUp className="w-5 h-5 shrink-0" data-id="element-148" />
          <h2 className="font-bold tracking-wide truncate" data-id="element-149">
            MONTHLY SCORECARD
          </h2>
        </div>
        <span className="text-xs sm:text-sm font-medium text-blue-200 shrink-0" data-id="element-150">
          May 1 – May 31, 2025
        </span>
      </div>

      <div className="p-5 flex-1 flex flex-col" data-id="element-151">
        {/* Top Stats */}
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 mb-6" data-id="element-152">
          <div className="flex-1 min-w-0" data-id="element-153">
            <p className="text-xs font-bold text-gray-500 uppercase mb-1 leading-tight" data-id="element-154">
              Total Points (Month To Date)
            </p>
            <div className="flex items-baseline gap-2 flex-wrap" data-id="element-155">
              <span className="text-5xl sm:text-6xl font-extrabold text-[#0a1e5e] tracking-tight leading-none" data-id="element-156">
                8,960
              </span>
              <span className="text-sm font-bold text-gray-500" data-id="element-157">POINTS</span>
            </div>
          </div>
          <div className="flex-1 bg-gray-50 rounded-xl p-4 border border-gray-200" data-id="element-158">
            <div className="flex justify-between items-center gap-2 mb-2 pb-2 border-b border-gray-200" data-id="element-159">
              <span className="text-xs font-bold text-gray-600 uppercase" data-id="element-160">
                Goal
              </span>
              <div className="flex items-baseline gap-1 shrink-0" data-id="element-161">
                <span className="text-lg font-extrabold text-[#0a1e5e]" data-id="element-162">
                  10,000
                </span>
                <span className="text-[10px] font-bold text-gray-500" data-id="element-163">PTS</span>
              </div>
            </div>
            <div className="flex justify-between items-center gap-2 mb-3" data-id="element-164">
              <span className="text-xs font-bold text-gray-600 uppercase" data-id="element-165">
                Remaining
              </span>
              <div className="flex items-baseline gap-1 shrink-0" data-id="element-166">
                <span className="text-lg font-extrabold text-[#0a1e5e]" data-id="element-167">
                  1,040
                </span>
                <span className="text-[10px] font-bold text-gray-500" data-id="element-168">PTS</span>
              </div>
            </div>
            <div data-id="element-169">
              <p className="text-[10px] font-bold text-gray-500 uppercase mb-1 leading-tight" data-id="element-170">
                % Of Goal Completed
              </p>
              <p className="text-xl font-extrabold text-green-600 leading-none" data-id="element-171">
                89.6%
              </p>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-8" data-id="element-172">
          <div className="flex justify-between items-end mb-2" data-id="element-173">
            <span className="text-xs font-bold text-[#0a1e5e] uppercase tracking-wider" data-id="element-174">
              Monthly Progress
            </span>
            <span className="text-sm font-bold text-green-600" data-id="element-175">89.6%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden" data-id="element-176">
            <div className="bg-green-600 h-4 rounded-full relative" style={{
            width: '89.6%'
          }} data-id="element-177">
              <div className="absolute right-0 top-0 bottom-0 w-4 bg-white/30" data-id="element-178"></div>
            </div>
          </div>
        </div>

        <h3 className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-4" data-id="element-179">
          Monthly Breakdown
        </h3>

        {/* Metric Tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-id="element-180">
          <MetricTile title="Credit Pulls" points={750} count={8} countLabel="Pulls" icon={FileText} colorClass="text-blue-700" bgClass="bg-blue-100" ptsPerAction={750} onClick={onTileClick ? () => onTileClick('credit_pull') : undefined} data-id="element-181" />
          <MetricTile title="Doc Outs" points={500} count={6} countLabel="Doc Outs" icon={Send} colorClass="text-green-600" bgClass="bg-green-100" ptsPerAction={500} onClick={onTileClick ? () => onTileClick('doc_out') : undefined} data-id="element-182" />
          <MetricTile title="Submissions" points={500} count={4} countLabel="Submissions" icon={Upload} colorClass="text-purple-600" bgClass="bg-purple-100" ptsPerAction={500} onClick={onTileClick ? () => onTileClick('submission') : undefined} data-id="element-183" />
          <MetricTile title="Closings" points={250} count={3} countLabel="Closings" icon={Home} colorClass="text-orange-500" bgClass="bg-orange-100" ptsPerAction={250} onClick={onTileClick ? () => onTileClick('closing') : undefined} data-id="element-184" />
        </div>

        {/* Speed Bonus MTD */}
        <div className="mt-auto bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center justify-between gap-3" data-id="element-185">
          <div className="flex items-center gap-3 min-w-0 flex-1" data-id="element-186">
            <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center shrink-0" data-id="element-187">
              <Clock className="w-6 h-6 text-white" data-id="element-188" />
            </div>
            <div className="min-w-0" data-id="element-189">
              <p className="text-sm font-bold text-[#0a1e5e] uppercase leading-tight" data-id="element-190">
                Speed Bonus{' '}
                <span className="text-gray-500 text-[10px] font-semibold" data-id="element-191">
                  (MTD)
                </span>
              </p>
              <p className="text-xs text-gray-600 leading-tight mt-0.5" data-id="element-192">
                Close a loan within 14 days of credit pull
              </p>
            </div>
          </div>
          <div className="text-right shrink-0" data-id="element-193">
            <div className="flex items-baseline gap-1 justify-end" data-id="element-194">
              <span className="text-2xl sm:text-3xl font-extrabold text-orange-500 leading-none" data-id="element-195">
                +500
              </span>
              <span className="text-sm font-bold text-gray-500" data-id="element-196">PTS</span>
            </div>
            <p className="text-[10px] font-bold text-[#0a1e5e] uppercase mt-1" data-id="element-197">
              1 Fast Close
            </p>
          </div>
        </div>
      </div>
    </motion.div>;
}